import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { Asset, AssetPage, MediaQuery } from '@mg/shared';
import { api } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';

/** The most pages {@link useMediaPages} fetches on its own, per list, to fill a filtered picker. */
export const MEDIA_AUTO_FILL_MAX = 5;

/**
 * The Media Library list, a page at a time (`GET /assets/page`), newest first.
 *
 * Cached under `['media', tenantKey, query, limit]`. The `['media', tenantKey]` prefix is what every
 * upload, move, delete and generation invalidates, so all of them keep refreshing it; the fourth
 * part keeps these entries apart from the plain lists the composer's media panel still caches under
 * `['media', tenantKey, query]` until it moves here too.
 *
 * `visible` is for a picker that shows only some of the files (ready JPG and PNG, say): while fewer
 * than `minVisible` (default: a page) of the loaded files pass it and more pages exist, the next page
 * is fetched on its own - a page that happens to be all SVG must not look like an empty library. It
 * stops after {@link MEDIA_AUTO_FILL_MAX} pages per list; past that, "Load more" is the user's. Pass a
 * STABLE function (module level, or memoised): it is an effect dependency.
 *
 * `refetchOnMount: 'always'` is for a list that must show what was added a moment ago (a picker
 * opened right after an upload): every loaded page is read again as the list mounts, however fresh
 * the cache. It is the list's own rule so that the reading-on above waits for it: a re-read asked
 * for from outside, after mounting, can only join a next-page read that is already on its way, and
 * that reads no loaded page again.
 */
export function useMediaPages(
  query: MediaQuery,
  limit: number,
  options: {
    visible?: (asset: Asset) => boolean;
    minVisible?: number;
    refetchOnMount?: 'always';
  } = {},
) {
  const tenantKey = useTenantKey();
  const { visible, minVisible = limit, refetchOnMount } = options;

  const result = useInfiniteQuery({
    queryKey: ['media', tenantKey, query, limit],
    queryFn: ({ pageParam, signal }) =>
      api.listMediaPage(query, { cursor: pageParam, limit }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: AssetPage) => last.nextCursor ?? undefined,
    enabled: tenantKey !== '',
    // Only when asked for: left out, the app-wide rule (read again once the cache is stale) stands.
    ...(refetchOnMount ? { refetchOnMount } : {}),
    // Poll while anything loaded is still generating (async providers finish later, by webhook), so
    // the finished file swaps in on its own. Every loaded page is re-read, not just the first: a
    // tile finishing further down would otherwise never swap in.
    refetchInterval: (q) =>
      q.state.data?.pages.some((page) => page.items.some((a) => a.status === 'generating'))
        ? 5000
        : false,
  });

  const pages = result.data?.pages;
  /** Every loaded page in order, each asset once - a file added between two pages can repeat one. */
  const assets = useMemo(() => {
    const seen = new Set<string>();
    const out: Asset[] = [];
    for (const page of pages ?? []) {
      for (const asset of page.items) {
        if (seen.has(asset.id)) continue;
        seen.add(asset.id);
        out.push(asset);
      }
    }
    return out;
  }, [pages]);
  // The newest page's count, and never fewer than are on screen (a delete between pages can do that).
  const total = Math.max(pages?.length ? pages[pages.length - 1].total : 0, assets.length);

  const { hasNextPage, isFetching, isFetchingNextPage, isError, fetchNextPage } = result;
  const visibleCount = useMemo(
    () => (visible ? assets.filter(visible).length : assets.length),
    [assets, visible],
  );
  const listKey = JSON.stringify([tenantKey, query, limit]);
  const autoFill = useRef({ listKey, fetched: 0 });
  // Re-run on every page that ARRIVES (`pageCount`), not on "fetching" going true and false: a fast
  // answer can land before React ever draws the fetching state, so nothing else would change and
  // the reading would stop after one page.
  const pageCount = pages?.length ?? 0;
  useEffect(() => {
    if (!visible) return;
    if (autoFill.current.listKey !== listKey) autoFill.current = { listKey, fetched: 0 };
    // Never while a read is in flight (a poll included), and never again after a failed one.
    if (!hasNextPage || isFetching || isError) return;
    if (visibleCount >= minVisible || autoFill.current.fetched >= MEDIA_AUTO_FILL_MAX) return;
    autoFill.current.fetched += 1;
    void fetchNextPage();
  }, [
    visible,
    listKey,
    pageCount,
    hasNextPage,
    isFetching,
    isError,
    visibleCount,
    minVisible,
    fetchNextPage,
  ]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    /** Every asset loaded so far, newest first. */
    assets,
    /** How many match the filters and the search, loaded or not. */
    total,
    hasMore: Boolean(hasNextPage),
    loadMore,
    isLoading: result.isLoading,
    isPending: result.isPending,
    /**
     * The list could not be read and none of it is on screen. Loaded assets are never given up for
     * an error: a failed "Load more" is `loadMoreFailed` instead, and a failed refresh keeps the
     * grid too. (The list goes on holding its last error while it is read again, so "any error but
     * a failed Load more" swapped the grid out on the first refresh after one.)
     */
    isError: result.isError && assets.length === 0,
    loadMoreFailed: result.isFetchNextPageError,
    error: result.error,
    isFetchingNextPage,
    refetch: result.refetch,
  };
}
